import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import { ShareDocumentDialog } from '../permission';
import { PermissionAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import './DocumentList.css';

interface Document {
  documentId: string;
  name: string;
  fileSize: number;
  pageCount: number;
  createdAt: string;
  folderId?: string;
}

interface DocumentListProps {
  currentFolderId?: string;
  onDocumentSelect?: (document: Document) => void;
  viewMode?: 'grid' | 'list';
  showActions?: boolean;
  className?: string;
  refreshKey?: number;
}

const DocumentList: React.FC<DocumentListProps> = ({
  currentFolderId,
  onDocumentSelect,
  viewMode = 'grid',
  showActions = true,
  className = '',
  refreshKey = 0
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareDialogDocument, setShareDialogDocument] = useState<Document | null>(null);
  const [shareCountMap, setShareCountMap] = useState<Record<string, number>>({});

  const { documents: documentsApi } = useApi();
  const { user } = useAuth();

  const transformDocument = (apiDoc: any): Document => ({
    documentId: apiDoc.document_id,
    name: apiDoc.name,
    fileSize: apiDoc.file_size,
    pageCount: apiDoc.page_count,
    createdAt: apiDoc.created_at,
    folderId: apiDoc.folder_id
  });

  useEffect(() => {
    loadData();
  }, [currentFolderId, refreshKey]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await documentsApi.getDocuments();
      const all = (data.documents || []).map(transformDocument);
      const filtered = all.filter(d =>
        currentFolderId ? d.folderId === currentFolderId : !d.folderId
      );
      setDocuments(filtered);

      // Fetch share counts for all documents in parallel (best-effort)
      if (user && filtered.length > 0) {
        Promise.all(
          filtered.map(async (doc) => {
            try {
              const permsData = await PermissionAPI.getDocumentPermissions(doc.documentId);
              return [doc.documentId, (permsData.permissions || []).length] as [string, number];
            } catch {
              return [doc.documentId, 0] as [string, number];
            }
          })
        ).then((entries) => setShareCountMap(Object.fromEntries(entries)));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentClick = (document: Document) => {
    if (onDocumentSelect) onDocumentSelect(document);
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString();

  const handleDeleteDocument = async (document: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${document.name}"? This cannot be undone.`)) return;
    try {
      setError('');
      await documentsApi.deleteDocument(document.documentId);
      setDocuments(prev => prev.filter(d => d.documentId !== document.documentId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete document');
    }
  };

  if (loading) return <LoadingSpinner message="Loading documents..." />;

  return (
    <div className={`document-list ${className}`}>
      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}

      <div className="documents-section">
        <h5 className="section-title">
          Documents {documents.length > 0 && `(${documents.length})`}
        </h5>

        {documents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <h6>No documents here</h6>
            <p className="text-muted">
              {currentFolderId
                ? 'This folder is empty. Upload a document above.'
                : 'Upload your first document to get started.'}
            </p>
          </div>
        ) : (
          <div className={`items-container ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
            {documents.map((document) => (
              <div
                key={document.documentId}
                className="item-card document-card"
                onClick={() => handleDocumentClick(document)}
              >
                <div className="item-content">
                  <div className="item-icon">📄</div>
                  <div className="item-details">
                    <h6 className="item-name" title={document.name}>{document.name}</h6>
                    <div className="item-meta">
                      {formatFileSize(document.fileSize)} · {document.pageCount} pages · {formatDate(document.createdAt)}
                    </div>
                  </div>
                </div>

                {showActions && (
                  <div className="item-actions">
                    <Link
                      to={`/documents/${document.documentId}`}
                      className="doc-btn doc-btn-view"
                      onClick={e => e.stopPropagation()}
                    >
                      Open
                    </Link>
                    <Link
                      to={`/documents/${document.documentId}/analytics`}
                      className="doc-btn doc-btn-secondary"
                      onClick={e => e.stopPropagation()}
                    >
                      Analytics
                    </Link>
                    {user && (
                      <Link
                        to={`/documents/${document.documentId}/permissions`}
                        className="doc-btn doc-btn-secondary share-btn-with-badge"
                        onClick={e => e.stopPropagation()}
                      >
                        Sharing
                        {(shareCountMap[document.documentId] ?? 0) > 0 && (
                          <span className="share-count-badge">
                            {shareCountMap[document.documentId]}
                          </span>
                        )}
                      </Link>
                    )}
                  </div>
                )}

                {showActions && user && (
                  <button
                    className="doc-btn doc-btn-delete"
                    onClick={e => handleDeleteDocument(document, e)}
                    title="Delete"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {shareDialogDocument && (
        <ShareDocumentDialog
          documentId={shareDialogDocument.documentId}
          documentName={shareDialogDocument.name}
          isOpen={!!shareDialogDocument}
          onClose={() => setShareDialogDocument(null)}
          onShareSuccess={() => setShareDialogDocument(null)}
        />
      )}
    </div>
  );
};

export default DocumentList;
